import * as React from "react";
import { Citation } from "../../../platform";
import CitationBadge from "./CitationBadge";
import { cn } from "../../../lib/utils";

/**
 * How many sources show before the rest fold away. A reply that cites a whole contract
 * lists thirty-odd chips, most of them a full line wide, which buries the next message
 * under the sources of this one. Six is two or three lines at pane width.
 */
export const SOURCES_VISIBLE = 6;

const chipClass =
  "inline-flex h-[22px] max-w-full items-center rounded-full px-2 text-[11px] font-medium whitespace-nowrap transition-colors";

/** The numbered sources under a reply, each a chip that takes you to it. */
const CitationSources: React.FC<{
  citations: Citation[];
  onNavigate: (citation: Citation) => void;
}> = ({ citations, onNavigate }) => {
  const [showAll, setShowAll] = React.useState(false);
  const folds = citations.length > SOURCES_VISIBLE + 1; // never fold away just one
  const visible = folds && !showAll ? citations.slice(0, SOURCES_VISIBLE) : citations;

  return (
    <div className="flex flex-row flex-wrap gap-1.5">
      {visible.map((cit, i) => (
        <button
          key={`cit-${i}`}
          type="button"
          onClick={() => onNavigate(cit)}
          title={cit.label}
          className={cn(chipClass, "bg-grey-100 text-foreground hover:bg-grey-200")}
        >
          {/* The label truncates, the badge does not: a chip a full line wide still
              shows which number it is. */}
          <span className="truncate">
            <span className="mr-1">
              <CitationBadge n={i + 1} />
            </span>
            {cit.label}
          </span>
        </button>
      ))}
      {folds && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className={cn(
            chipClass,
            "border border-input bg-transparent font-semibold text-muted-foreground hover:bg-accent"
          )}
        >
          {showAll ? "Show fewer" : `+${citations.length - SOURCES_VISIBLE} more sources`}
        </button>
      )}
    </div>
  );
};

export default CitationSources;
